import type React from 'react';

import { Card } from '~/components/ui/Card';
import { Container } from '~/components/ui/Container';
import { Icon } from '~/components/ui/Icon';
import type { IconName } from '~/components/ui/Icon';
import { Reveal } from '~/components/ui/Reveal';
import { SectionHeader } from '~/components/ui/SectionHeader';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/**
 * WhyArpa — 4 USP cards in a 2×2 desktop grid.
 * id="why-arpa" is used for accessibility; not in nav (by design).
 */
function WhyArpa(): React.ReactElement {
  const content = useContent();
  const { whyArpa } = content;

  return (
    <section
      id="why-arpa"
      className={cn('bg-[var(--color-void-navy)]', 'py-24 md:py-32', 'scroll-anchor')}
      aria-labelledby="why-arpa-heading"
    >
      <Container>
        <SectionHeader
          eyebrow={whyArpa.eyebrow}
          heading={whyArpa.heading}
          align="left"
          id="why-arpa-heading"
          className="mb-14 md:mb-16 max-w-2xl"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {whyArpa.items.map((item, i) => (
            <Reveal key={item.title} delay={i * 80}>
              <Card variant="default" className="h-full">
                {/* Icon — 48px, Cyan Signal */}
                <div className="mb-5 text-[var(--color-cyan-signal)]" aria-hidden="true">
                  <Icon name={item.icon as IconName} size={48} />
                </div>

                {/* Title */}
                <h3
                  className={cn(
                    'font-head font-semibold text-2xl tracking-[0.05em]',
                    'text-[var(--color-frost-white)]',
                    'mb-3'
                  )}
                >
                  {item.title}
                </h3>

                {/* Body */}
                <p
                  className={cn(
                    'font-body font-light leading-[1.8] text-base',
                    'text-[var(--color-steel-mist)]'
                  )}
                >
                  {item.body}
                </p>
              </Card>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

export { WhyArpa };
export default WhyArpa;
